/**
 * User Model Instance
 */

define(function(require) {
    'use strict';

    var $ = require('j');
    var _ = require('underscore');
    var core = require('core');
    var BaseModel = require('common/base.model');
    var server = require('server');
    var langUtil = require('util/language.util');
    var device = require('util/device.util');
    var urlUtil = require('util/url.util');
    var storage = require('util/storage.util');
    var historyUtil = require('util/history.util');
    var SegmentationService = require('modules/segmentation/segmentation.service');
    var config = require('config');
    const { dispatcher } = require('bundle');

    /**
     * @class
     * @name UserModel
     * @augments BaseModel
     */
    var UserModel = BaseModel.extend(/** @lends UserModel.prototype */ {

        /**
         * Array with properties, that will be available after logout
         * @type {Array}
         */
        cachedAttributes: [
            'lang',
            'hideBalance',
            'hasBeenLoggedIn',
            'suspended',
            'clientIp',
            'daydepositlimit',
            'weekdepositlimit',
            'monthdepositlimit',
            'currencyCode',
            'country',
            'detectedCountry',
            'langDetected',
            'promoCode',
            'registrationInfo',
            'userAvailabilityVerificationToken',
            'emailAvailabilityVerificationToken',
            'signUpVerificationToken'
        ],

        idAttribute: 'sessionToken',

        rememberMeStorageKey: 'hub2_username_rememberMe',

        defaults: function() {
            return {
                isLoggedIn: false,
                role: 'guest',
                currencyCode: config.get('defaultCurrency'),
                lang: config.get('defaultLocale'),
                country: config.get('defaultCountry')
            };
        },

        initialize: function() {
            this._verifyDefaultCmsValues();
            this.segmentationService = new SegmentationService(this);

            this._setLanguageFromUrl();
            this._restoreCachedModel();

            // logout user, is needed
            var urlParams = urlUtil.getURLParams();
            if (urlParams.logout || urlParams.temptoken === '') {
                var trimmedURLParams = urlUtil.serialize(_.omit(urlParams, 'logout', 'temptoken', 'username'));
                historyUtil.replaceState(null, null, urlUtil.pathname + '?' + trimmedURLParams + location.hash);
                if (this.get('role') === 'user') {
                    core.request('auth:action:quietLogout');
                }
            }

            this._updateLocale();
        },

        subscribe: function() {
            this.on({
                'change': this._saveToStorage,
                'change:lang': this._updateLocale,
                'change:isLoggedIn': this._dispatchAuthEvents
            });

            this.listenTo(core,
                {
                    'comppoints:updated deposit:success deposit:fail withdraw:success withdraw:fail':
                        this.segmentationService.getUrlTemplates.bind(this.segmentationService, null),
                    'user:getPaymentsConfiguration:success': function(response) {
                        this.set('paymentsConfig', response.data.casinoConfiguration.casino);
                    }
                }
            );

            core.reply('user:attributes', this.toJSON.bind(this));

            this.listenTo(core, 'auth:reject', function() {
                var localUser = storage.session.getItem('userModel');
                this.set(localUser);
            });

            // check for admin panel
            dispatcher?.on('user:sync:change', (userAttrs) => {
                BaseModel.prototype.set.call(this, userAttrs);
            });
            dispatcher?.on('user:sync:delete', (userAttrs) => {
                BaseModel.prototype.set.call(this, userAttrs, {
                    unset: true
                });
            });
        },

        set: function(key, val, options) {
            BaseModel.prototype.set.apply(this, arguments);

            dispatcher?.dispatch('user:set', key, val, options);

            return this;
        },

        _verifyDefaultCmsValues: function() {
            // can't throw here or we won't be able to enter admin panel to fix an issue
            var message = ' is not set correctly. Check your CMS settings.';

            var defaultCurrency = config.get('defaultCurrency');
            if (!defaultCurrency || !config.get('allowedCurrencies').includes(defaultCurrency)) {
                core.error('"defaultCurrency"' + message);
            }

            var defaultLocale = config.get('defaultLocale');
            if (!defaultLocale || !config.get('allowedLocales').includes(defaultLocale)) {
                core.error('"defaultLocale"' + message);
            }

            var defaultCountry = config.get('defaultCountry');
            if (!defaultCountry || !_.find(config.get('allowedCountries'), { code: defaultCountry })) {
                core.error('"defaultCountry"' + message);
            }
        },

        _restoreCachedModel: function() {
            var cachedModel = this.getCachedModel();
            if (!cachedModel) {
                return;
            }

            if (cachedModel.country) {
                cachedModel._countryDetected = true;
            }

            // if language was in URL - ignore it in cached model
            if (this.get('langDetected')) {
                delete cachedModel.lang;
            } else if (cachedModel.lang) {
                cachedModel.langDetected = true;
            }

            if (cachedModel.currencyCode) {
                cachedModel._currencyCodeDetected = true;
            }

            this.set(cachedModel);
        },

        /**
         * Get user model from session storage
         */
        getCachedModel: function() {
            var cachedModel = storage.session.getItem('userModel');
            if (!cachedModel) {
                return null;
            }

            // validate cached attributes
            if (!langUtil.getLanguage(cachedModel.lang)) {
                delete cachedModel.lang;
            }
            if (!_.findWhere(config.get('allowedCountries'), { code: cachedModel.country })) {
                delete cachedModel.country;
            }
            if (!_.contains(config.get('allowedCurrencies'), cachedModel.currencyCode)) {
                delete cachedModel.currencyCode;
            }

            return cachedModel;
        },

        /**
         * @deprecated 16.8.0
         * Use `auth` for same functionality. (login/logout/register)
         */
        sync: function(method, model, options) {
            if (method === 'read') {
                return core.request('auth:action:login', options, true);
            } else if (method === 'create') {
                return core.request('auth:action:register', options, true);
            } else if (method === 'update') {
                return this._update(model, options);
            }
        },

        _setLanguageFromUrl: function() {
            var language = urlUtil.parseLang(urlUtil.pathname);
            if (!language) {
                return;
            }

            this.set('langDetected', true);
            this.set('lang', language);
        },

        _dispatchAuthEvents: function() {
            if (!this.previous('isLoggedIn') && this.get('isLoggedIn')) {
                _.delay(function() { dispatcher.dispatch('auth:login'); }, 100);
            } else if (this.previous('isLoggedIn') && !this.get('isLoggedIn')) {
                _.delay(function() { dispatcher.dispatch('auth:logout'); }, 100);
            }
        },

        /**
         * Save user model to session storage
         * @private
         */
        _saveToStorage: function() {
            storage.session.setItem('userModel', _.omit(this.toJSON(),
                'isLoggedIn', 'lowBalanceHighlight', 'showLowBalancePopup'
            ));
        },

        /**
         * Update user info in IMS. Used in model sync
         * @private
         * @param  {Model}  model   user model
         * @param  {Object} options
         * @return {Deferred}
         */
        _update: function(model, options) {
            return server.send({
                ID: 33045,
                description: 'Update player info',
                responses: [33006, 31008],
                changeTimestamp: window.Playtech.API.formatter.getDateForIms(new Date()),
                dataMap: options.userDetails || this.get('userDetails')
            }).done(options.success).fail(options.error);
        },

        /**
         * Get user details from IMS
         * @param {Array} playerFields Array with required player data fields
         * @return {Promise}
         */
        fetchUserDetails: function(playerFields) {

            return window.Playtech.API.user.getPlayerData(playerFields)
                .then((response) => {
                    const userDetails = response.data;

                    this.set({
                        userDetails: userDetails,
                        currencyCode: userDetails.currency,
                        country: userDetails.countryCode
                    });

                    return response;
                });
        },

        /**
         * Return player actual login status
         * @returns {boolean}
         */
        isLoggedIn: function() {
            return Boolean(this.get('isLoggedIn'));
        },

        /**
         * Check login
         * @deprecated since 17.1.0 use `isLoggedIn`
         * @return {boolean}
         */
        isLogin: function() {
            return this.isLoggedIn();
        },

        /**
         * Check admin role
         * @deprecated 16.8.0 use auth `isAdmin`
         * @return {boolean}
         */
        isAdmin: function() {
            return core.request('auth:isAdmin');
        },

        /**
         * Check if user fast registered
         * @return {boolean}
         */
        isFastRegistered: function() {
            return config.get('enableFastRegistration')
                && Boolean(this.get('userDetails') && Number(this.get('userDetails').custom19));
        },

        _updateLocale: function() {
            urlUtil.setLocale(this.get('lang'));
        },

        /**
         * Get promocode from server to fill user's cash balance
         * @param {string} data Promocode which input user in the field
         * @return {Deferred}
         */
        getPromoCode: function(data) {
            return server.send({
                ID: 31065,
                triggerType: 'PromotionCode',
                promotionCode: data.promoCode,
                responses: [31066, 31067],
                description: 'Get promo code'
            });
        },

        /**
         * Get player lifetime statistics
         * @return {Deferred}
         */
        getPlayerLifetimeStatistics: function(statisticsType) {
            var state = $.Deferred();
            var reqName = 'Get player lifetime statistics';

            server.send({
                    ID: 32032,
                    description: reqName,
                    responses: [32033, 32034],
                    statisticsTypes: [statisticsType],
                    clientType: config.get('clientType'),
                    clientPlatform: device.clientPlatform
                })
                .done(function(resp) {
                    state.resolve(resp.data.statisticsList[0]);
                })
                .fail(function(error) {
                    core.error(reqName, error);
                    state.reject();
                });

            return state.promise();
        },

        /**
         * Continue Registration
         * @param {boolean} isGame flag
         * @param  {Object} options
         */
        continueRegistration: function(isGame, options) {
            this.set('afterContinueRegisterAction', this.setAfterContinueRegisterAction(isGame, options));
            core.navigate(config.get('dynamicTags.DTAG_CONTINUE_REGISTER_URL') || '/');
        },

        /**
         * Set After Continue Registration Action
         * @param {boolean} isGame flag
         * @param  {Object} options
         */
        setAfterContinueRegisterAction: function(isGame, options) {
            if (isGame) {
             return {
                    action: 'launchGame',
                    options: options
                };
            }

            return {
                action: 'redirect',
                url: options && options.href || urlUtil.href
            };
        },

        /**
         * Return tags by tag name
         * @param {string} tagName
         */
        getPlayerTags: function(tagName) {
            return server.send({
                ID: 35547,
                tagName: tagName,
                description: 'Get Player Tags',
                responses: [35548, 35549]
            });
        },

        storeRememberMeItem: function() {
            storage.local.setItem(this.rememberMeStorageKey, this.get('userName'));
        },

        retrieveRememberMeItem: function() {
            return storage.local.getItem(this.rememberMeStorageKey);
        },

        removeRememberMeItem: function() {
            storage.local.removeItem(this.rememberMeStorageKey);
        }
    });

    return UserModel;
});
